import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InviteUserInput, UserRole, UserStatus } from '@crm/shared';
import { api } from '../../api/client';

export interface UserRow {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  photoFileId: string | null;
  lastLoginAt: string | null;
  inviteExpiresAt: string | null;
  createdAt: string;
}

const KEY = ['users'];

export function useUsers() {
  return useQuery({ queryKey: KEY, queryFn: async () => (await api.get<UserRow[]>('/users')).data });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InviteUserInput) =>
      (await api.post<UserRow & { inviteLink: string; emailError: string | null }>('/users/invite', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ inviteLink: string; emailError: string | null }>(`/users/${id}/resend-invite`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; role?: UserRole; status?: 'ACTIVE' | 'DISABLED' }) =>
      (await api.patch<UserRow>(`/users/${id}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
